import { useMemo } from 'react'
import { Cell, Column } from 'react-table'
import { Badge, Button, HStack } from '@ritualco/jutsu'
import { BasicTable } from 'components/BasicTable'
import { useContent } from 'lib/content/react'
import { WorkspaceMember } from 'lib/generated/api'

type ActionsProps = {
  workspaceMembers: WorkspaceMember
  onResend: () => void
  onRemove: () => void
}

export type WorkspaceMembersTableProps = {
  workspaceMembers?: WorkspaceMember[]
  onResend: (connectionId: string) => void
  onRemove: (connectionId: string) => void
}

const Actions = ({ workspaceMembers, onResend, onRemove }: ActionsProps) => {
  const { formatMessage } = useContent()
  const showResendButton = workspaceMembers.status === 'INVITED'
  const showRemoveButton = workspaceMembers.status !== 'REVOKED'

  return (
    <HStack spacing={2} justifyContent="flex-end">
      {showResendButton && (
        <Button variant="ghost" size="sm" onClick={onResend}>
          {formatMessage({ id: 'pmportal.companies.resend' })}
        </Button>
      )}
      {showRemoveButton && (
        <Button
          colorScheme="transparent"
          variant="outline"
          size="sm"
          onClick={onRemove}
        >
          {formatMessage({ id: 'pmportal.global.actionRemove' })}
        </Button>
      )}
    </HStack>
  )
}

export const MembersTable = ({
  workspaceMembers = [],
  onResend,
  onRemove,
}: WorkspaceMembersTableProps) => {
  const { formatMessage } = useContent()
  const columns = useMemo<Column<WorkspaceMember>[]>(
    () => [
      {
        Header: formatMessage({ id: 'pmportal.employees.contactEmail' }),
        id: 'connectionId',
        accessor: row => row.email,
      },
      {
        Header: formatMessage({ id: 'pmportal.employees.status' }),
        accessor: 'status',
        Cell: ({ value }) => {
          if (value === 'ACTIVE') {
            return (
              <Badge>
                {formatMessage({ id: 'pmportal.employees.acceptedStatus' })}
              </Badge>
            )
          } else if (value === 'REVOKED') {
            return (
              <Badge colorScheme="red" variant="outline">
                {formatMessage({ id: 'pmportal.employees.deletedStatus' })}
              </Badge>
            )
          } else {
            return (
              <Badge colorScheme="gray">
                {formatMessage({ id: 'pmportal.employees.invitedStatus' })}
              </Badge>
            )
          }
        },
      },
      {
        id: 'actions',
        Cell: ({ row }: Cell<WorkspaceMember>) => (
          <Actions
            workspaceMembers={row.original}
            onResend={() => {
              onResend(row.original.workspaceMemberId)
            }}
            onRemove={() => {
              onRemove(row.original.workspaceMemberId)
            }}
          />
        ),
      },
    ],
    [formatMessage, onResend, onRemove]
  )

  return (
    <BasicTable
      columns={columns}
      data={workspaceMembers}
      placeholder={formatMessage({
        id: 'pmportal.global.emptyTable',
      })}
      tdProps={() => ({ lineHeight: 'inherit' })}
    />
  )
}
